/**
 * Noibu Feature Flag — paid landing experiment ("collection-landing").
 *
 * Structurally identical to assets/noibu-flag-cart.js and
 * assets/noibu-feature-flag.js. Kept as its own file for the same reason: each
 * of those defines the control for a different experiment, and folding a new
 * flag into a running one changes its control mid-flight.
 *
 * Both experiences are server-rendered by Liquid. This script builds no DOM;
 * it sets a class on <html> that decides whether the already-rendered landing
 * header is visible. A flag failure is a no-op and the collection page renders
 * exactly as it does today.
 *
 *   current-experience  -> html.nff-collection--a  (control, the default)
 *   show-landing        -> html.nff-collection--b
 *
 * AUDIENCE LIVES IN THE FLAG, NOT HERE
 * This experiment is meant for paid social traffic (utm_medium=paid). That
 * targeting is configured on the flag in the Noibu console, not in the theme.
 * Do not add a UTM check to this file: it would bucket visitors the console
 * thinks it excluded, and the two populations would disagree.
 *
 * PREREQUISITE — READ THIS BEFORE ASSUMING THE TEST IS RUNNING
 * NOIBUJS_CONFIG.feature_flag_key must be provisioned for this store. Without
 * it, collect-core never injects collect-ff.js, window.NoibuFeatureFlag is
 * never defined, and nobody is bucketed. The home-page experiment recorded
 * zero exposures between 2026-08-21 and 2026-08-26 for exactly this reason,
 * while the page looked perfectly healthy. reportState() makes that state
 * visible in Explorations instead of silent.
 */
(function () {
  'use strict';

  var FLAG = '2083-collection-landing';
  var CONTROL = 'current-experience';
  var VARIATION_B = 'show-landing';
  var CACHE_KEY = 'nff.collection-landing';

  var REPORT_KEY = 'nff_collection-landing';
  var STATE_ATTR = 'data-nff-collection-state';

  var SDK_TIMEOUT_MS = 10000;

  var CLASSES = {};
  CLASSES[CONTROL] = 'nff-collection--a';
  CLASSES[VARIATION_B] = 'nff-collection--b';

  var root = document.documentElement;

  function reportState(state, detail) {
    root.setAttribute(STATE_ATTR, state);

    if (state === 'unavailable') {
      console.warn('[nff] ' + FLAG + ' flag unavailable, rendering control: ' + detail);
    }

    // addCustomAttribute comes from collect-core, which loads independently of
    // collect-ff — so reporting works in precisely the case where flags do not.
    var tries = 0;
    (function attempt() {
      try {
        if (window.NOIBUJS && typeof window.NOIBUJS.addCustomAttribute === 'function') {
          window.NOIBUJS.addCustomAttribute(REPORT_KEY, state);
          return;
        }
      } catch (error) {
        // Not terminal — collect-core may still be booting.
      }
      if (++tries < 40) setTimeout(attempt, 500);
    })();
  }

  function afterLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    var fired = false;
    window.addEventListener('load', function () {
      if (fired) return;
      fired = true;
      callback();
    });
  }

  function whenClientReady(onReady, onUnavailable) {
    var done = false;
    var timer = null;

    function finish(handler, argument) {
      if (done) return;
      done = true;
      if (timer !== null) clearTimeout(timer);
      handler(argument);
    }

    afterLoad(function () {
      if (done) return;
      timer = setTimeout(function () {
        finish(
          onUnavailable,
          'no client ' + SDK_TIMEOUT_MS + 'ms after window.load — is ' +
            'NOIBUJS_CONFIG.feature_flag_key provisioned for this store?'
        );
      }, SDK_TIMEOUT_MS);
    });

    window.addEventListener('noibuFeatureFlagError', function (event) {
      var message = (event && event.detail && event.detail.message) || 'unknown error';
      finish(onUnavailable, message);
    });

    if (window.NoibuFeatureFlag) {
      try {
        var client = window.NoibuFeatureFlag.getClient();
        client.addHandler('PROVIDER_READY', function () {
          finish(onReady, client);
        });
      } catch (error) {
        finish(onUnavailable, 'getClient() threw: ' + error);
      }
      return;
    }

    window.addEventListener('noibuFeatureFlagReady', function (event) {
      var ready = event && event.detail && event.detail.client;
      if (ready) finish(onReady, ready);
      else finish(onUnavailable, 'ready event carried no client');
    });
  }

  function activate(variation) {
    var className = CLASSES[variation] || CLASSES[CONTROL];
    if (root.classList.contains(className)) return;

    root.classList.remove(CLASSES[CONTROL], CLASSES[VARIATION_B]);
    root.classList.add(className);
    root.setAttribute('data-nff-collection', variation);

    document.dispatchEvent(
      new CustomEvent('noibu:collection-variation', { detail: { variation: variation } })
    );
  }

  function remember(variation) {
    // Paid traffic usually lands once, but a visitor who clicks two ads in a
    // week should not see the page change shape between them. Wrapped because
    // storage throws in private mode and in the in-app browsers that most of
    // this traffic arrives through.
    try {
      window.localStorage.setItem(CACHE_KEY, variation);
    } catch (error) {
      /* no-op */
    }
  }

  function forcedVariation() {
    // QA override: ?nff_collection=show-landing.
    try {
      var value = new URLSearchParams(window.location.search).get('nff_collection');
      if (value === CONTROL || value === VARIATION_B) return value;
    } catch (error) {
      /* URLSearchParams unavailable — fall through to the flag. */
    }
    return null;
  }

  function render(client) {
    var variation = client.getStringValue(FLAG, CONTROL);

    if (variation === VARIATION_B) {
      activate(VARIATION_B);
      remember(VARIATION_B);
      return VARIATION_B;
    }

    activate(CONTROL);
    remember(CONTROL);
    return CONTROL;
  }

  var forced = forcedVariation();
  if (forced) {
    // No remember(), no SDK call: a forced preview must not poison the cache
    // or record an exposure that would skew the experiment's own numbers.
    activate(forced);
    root.setAttribute(STATE_ATTR, 'forced');
    return;
  }

  whenClientReady(
    function (client) {
      reportState(render(client));

      client.addHandler('PROVIDER_CONFIGURATION_CHANGED', function () {
        render(client);
      });
    },
    function (detail) {
      // Do NOT remember() a value the SDK never produced — it would hide the
      // failure on every later page view.
      if (!root.hasAttribute('data-nff-collection')) activate(CONTROL);
      reportState('unavailable', detail);
    }
  );
})();
